import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Upload, Link as LinkIcon, Building2 } from "lucide-react";
import { fetchClients, createClient, updateClient, deleteClient, uploadClientLogo, absoluteUrl } from "@/lib/api";
import { formatApiErrorDetail } from "@/contexts/AuthContext";

const TYPES = ["Hospital", "Pharmacy", "Clinic", "Organization"];
const empty = { name: "", type: "Hospital", description: "", logo_text: "", logo_url: "", case_study: "" };

export default function ClientsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try { setItems(await fetchClients()); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    try {
      await deleteClient(id);
      toast.success("Client deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-clients">
      <div className="flex items-end justify-between gap-4 mb-7">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Clients</h1>
          <p className="text-slate-500 mt-1 text-sm">Hospitals, pharmacies and partners showcased on your site & /clients page.</p>
        </div>
        <button onClick={() => setEditing(empty)} data-testid="clients-add" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2.5 text-sm font-medium shadow-md shadow-blue-600/20 shrink-0">
          <Plus size={16} /> Add Client
        </button>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => <div key={i} className="h-44 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="text-slate-500 py-10 text-center">No clients yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((c) => (
            <article key={c.id} data-testid={`admin-client-${c.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex gap-3 mb-3">
                {c.logo_url ? (
                  <img src={absoluteUrl(c.logo_url)} alt={c.name} className="w-12 h-12 rounded-lg object-cover bg-slate-50 border border-slate-100" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-500 text-white font-heading font-bold flex items-center justify-center shrink-0">
                    {c.logo_text}
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-slate-900 truncate">{c.name}</h3>
                  <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5"><Building2 size={11} /> {c.type}</div>
                </div>
              </div>
              <p className="text-sm text-slate-600 line-clamp-2">{c.description}</p>
              <div className="flex gap-2 mt-4">
                <button data-testid={`client-edit-${c.id}`} onClick={() => setEditing(c)} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-50"><Pencil size={13} /> Edit</button>
                <button data-testid={`client-delete-${c.id}`} onClick={() => handleDelete(c.id)} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50"><Trash2 size={13} /> Delete</button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && <ClientDialog initial={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  );
}

function ClientDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...empty, ...initial });
  const [saving, setSaving] = useState(false);
  const [logoMode, setLogoMode] = useState(initial.logo_url && !/^https?:\/\//i.test(initial.logo_url) ? "upload" : (initial.logo_url ? "url" : "upload"));
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!initial.id;
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadClientLogo(file);
      setForm((f) => ({ ...f, logo_url: url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, logo_url: form.logo_url || null };
      if (isEdit) {
        await updateClient(initial.id, payload);
        toast.success("Client updated");
      } else {
        await createClient(payload);
        toast.success("Client created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-testid="client-dialog">
      <form onSubmit={onSave} className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-heading text-xl font-semibold text-slate-900">{isEdit ? "Edit Client" : "Add Client"}</h2>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company Name *" name="name" value={form.name} onChange={onChange} required testid="client-input-name" />
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Type *</label>
              <select name="type" value={form.type} onChange={onChange} data-testid="client-input-type" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Description *</label>
            <textarea name="description" value={form.description} onChange={onChange} required rows={2} data-testid="client-input-description" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <Field label="Logo Initials (fallback) *" name="logo_text" value={form.logo_text} onChange={onChange} required testid="client-input-logo-text" placeholder="e.g. SM" />

          {/* Logo toggle */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">Logo Artwork</label>
            <div className="inline-flex bg-slate-100 rounded-full p-1 mb-3">
              <button type="button" onClick={() => setLogoMode("upload")} data-testid="logo-mode-upload" className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs rounded-full ${logoMode === "upload" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
                <Upload size={12} /> Upload File
              </button>
              <button type="button" onClick={() => setLogoMode("url")} data-testid="logo-mode-url" className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs rounded-full ${logoMode === "url" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
                <LinkIcon size={12} /> Image URL
              </button>
            </div>

            {logoMode === "upload" ? (
              <div className="flex items-center gap-3">
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="client-input-logo-file" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 border border-slate-200 rounded-xl px-4 py-2.5 text-sm hover:bg-slate-50 disabled:opacity-70">
                  {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Choose file</>}
                </button>
                {form.logo_url && <img src={absoluteUrl(form.logo_url)} alt="logo" className="w-12 h-12 rounded-lg object-cover border border-slate-200" />}
              </div>
            ) : (
              <input
                type="url"
                name="logo_url"
                value={form.logo_url || ""}
                onChange={onChange}
                placeholder="https://example.com/logo.png"
                data-testid="client-input-logo-url"
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
            {form.logo_url && (
              <button type="button" onClick={() => setForm((f) => ({ ...f, logo_url: "" }))} className="text-xs text-red-500 mt-2 hover:underline">Remove logo</button>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Case Study (optional)</label>
            <textarea name="case_study" value={form.case_study || ""} onChange={onChange} rows={3} data-testid="client-input-case-study" placeholder="Share results & outcomes…" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-6 border-t border-slate-100 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button type="submit" disabled={saving} data-testid="client-save" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 text-sm font-medium rounded-lg disabled:opacity-70">
            {saving && <Loader2 size={14} className="animate-spin" />} Save
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, name, value, onChange, required, testid, placeholder, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">{label}</label>
      <input type={type} name={name} value={value || ""} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid}
        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
    </div>
  );
}
